/**
 * <cart-drawer> — coque du panier coulissant, bâtie sur <dialog> natif.
 *
 * <dialog>.showModal() fournit gratuitement la fermeture par Échap et le fond
 * assombri (::backdrop), mais PAS un piège de focus qui boucle (Tab en fin de
 * liste renvoie vers <body>) — et rien ne peut intercepter le Tab qui sort d'un
 * iframe cross-origin (boutons de paiement express Shop Pay / PayPal) : le
 * keydown se produit dans le document de l'iframe, invisible au nôtre. Une fois
 * le focus tombé sur <body> (devenu inert), Échap cesse même de fermer le
 * tiroir. D'où la surveillance périodique de #startFocusWatch, seul mécanisme
 * qui détecte cette sortie (vérifié : ni focusin ni focusout ne se déclenchent
 * sur cette transition précise).
 *
 * Ce composant gère donc :
 *   - l'ouverture (clic sur [data-cart-open] du header, ou événement cart:open
 *     émis par cart-items.js après un ajout) ;
 *   - la fermeture (bouton [data-cart-close], clic sur le fond) ;
 *   - le retour du focus sur l'élément déclencheur à la fermeture ;
 *   - le piège de focus (surveillance périodique, tant que la dialog est ouverte).
 *
 * Le contenu (lignes, jauge, résumé…) est géré par <cart-items> à l'intérieur.
 *
 * Progressive enhancement : sans JavaScript, l'icône panier du header reste un
 * lien vers /cart ; ce composant n'est jamais sollicité.
 */
const FOCUS_WATCH_MS = 100;

class CartDrawer extends HTMLElement {
  #dialog = null;
  #opener = null;
  #focusWatchId = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.querySelectorAll('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Clic sur le fond assombri : la cible est alors la <dialog> elle-même.
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close();
    });

    // Se déclenche pour toute fermeture (notre close(), mais aussi Échap natif) :
    // la surveillance doit s'arrêter dans les deux cas.
    this.#dialog.addEventListener('close', () => this.#stopFocusWatch());

    // Referme avant toute navigation (retour arrière du navigateur inclus).
    window.addEventListener('pageshow', () => this.close());
  }

  open(opener) {
    if (!this.#dialog || this.#dialog.open) return;
    this.#opener = opener || document.activeElement;
    this.#dialog.showModal();
    this.#startFocusWatch();
  }

  close() {
    if (!this.#dialog || !this.#dialog.open) return;
    this.#dialog.close();
    if (this.#opener instanceof HTMLElement) this.#opener.focus();
    this.#opener = null;
  }

  #startFocusWatch() {
    const closeButton = this.querySelector('[data-cart-close]');
    this.#focusWatchId = setInterval(() => {
      if (!this.#dialog.contains(document.activeElement)) closeButton?.focus();
    }, FOCUS_WATCH_MS);
  }

  #stopFocusWatch() {
    clearInterval(this.#focusWatchId);
    this.#focusWatchId = null;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Déclencheurs globaux, liés une seule fois (le composant peut être re-rendu).
if (!window.__cartDrawerBound) {
  window.__cartDrawerBound = true;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (!opener) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return; // pas de drawer (ex. page /cart) : on laisse le lien agir
    event.preventDefault();
    drawer.open(opener);
  });

  // Ouverture automatique après un ajout réussi (émis par cart-items.js).
  document.addEventListener('cart:open', () => {
    document.querySelector('cart-drawer')?.open();
  });
}
